import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { tasksAPI, jobTypesAPI, teamsAPI, usersAPI, availabilityAPI, resourcesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import KanbanColumn from '../components/KanbanColumn';
import DatePicker from '../components/DatePicker';
import { getTodayKualaLumpurISODate } from '../utils/dateFormat';
import styles from '../styles/TasksPage.module.css';

const normalizeDateOnly = (value) => String(value || '').split('T')[0];

const formatMemberLabel = (member) => {
  const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
  return fullName ? `${fullName} (${member.username})` : member.username;
};

const formatClockInTime = (value) => {
  if (!value) return '';

  const raw = String(value);
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.slice(0, 5);
  }

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '';

  return dt.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const parseMemberIds = (task) => {
  if (!task?.member_ids) return [];
  return String(task.member_ids)
    .split(',')
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

const toUniqueIds = (arr) => Array.from(new Set((arr || [])
  .map((id) => Number(id))
  .filter((id) => Number.isFinite(id) && id > 0)));

const normalizeTaskResourceItems = (items) => {
  const map = new Map();
  for (const item of items || []) {
    const resourceId = Number(item?.resourceId);
    const quantity = Number(item?.quantity);
    if (!Number.isFinite(resourceId) || resourceId <= 0) continue;
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    map.set(resourceId, (map.get(resourceId) || 0) + quantity);
  }
  return Array.from(map.entries()).map(([resourceId, quantity]) => ({ resourceId, quantity }));
};

const createEmptyClockInPopup = () => ({
  open: false,
  sourceMode: 'drag',
  targetStatus: 'in_progress',
  taskId: null,
  taskTitle: '',
  taskDate: '',
  plannedAvailableMembers: [],
  clockedInMembers: [],
  notClockedInMembers: [],
  pendingPayload: null,
  submitting: false,
});

const TasksPage = () => {
  const { user, can, refreshPermissions } = useAuth();
  const { t } = useLocalization();
  const canViewFinancials = can('financials', 'view');
  const canViewTasks = can('tasks', 'view');
  const canManageTasks = can('tasks', 'create') || can('tasks', 'edit');
  const today = getTodayKualaLumpurISODate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [visibleStatuses, setVisibleStatuses] = useState({
    pending: true,
    in_progress: true,
    completed: true,
    cancelled: false,
  });
  const [collapsedView, setCollapsedView] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [jobTypes, setJobTypes] = useState([]);
  const [resourcesCatalog, setResourcesCatalog] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editTaskMemberIds, setEditTaskMemberIds] = useState([]);
  const [newTaskResourceInput, setNewTaskResourceInput] = useState({ resourceId: '', quantity: 1 });
  const [newTaskResources, setNewTaskResources] = useState([]);
  const [editTaskResourceInput, setEditTaskResourceInput] = useState({ resourceId: '', quantity: 1 });
  const [editTaskResources, setEditTaskResources] = useState([]);
  const [newTask, setNewTask] = useState({
    taskDate: getTodayKualaLumpurISODate(),
    title: '',
    description: '',
    revenueAmount: '',
    jobTypeId: '',
    teamId: '',
    priority: 'medium',
    startTime: '',
    endTime: ''
  });
  const [editTask, setEditTask] = useState({
    id: '',
    originalStatus: 'pending',
    taskDate: '',
    title: '',
    description: '',
    revenueAmount: '',
    jobTypeId: '',
    teamId: '',
    status: 'pending',
    priority: 'medium',
    startTime: '',
    endTime: '',
  });
  const [clockInPopup, setClockInPopup] = useState(createEmptyClockInPopup);
  const fetchTasksRequestIdRef = useRef(0);

  const resolveMembersByIds = async (ids) => {
    const uniqueIds = toUniqueIds(ids);
    if (uniqueIds.length === 0) return [];

    try {
      const response = await usersAPI.getAll();
      const allUsers = response.data || [];
      const byId = new Map(allUsers.map((u) => [Number(u.id), u]));
      return uniqueIds.map((id) => {
        const user = byId.get(id);
        if (!user) return { user_id: id, username: `${t('tasks_user_fallback', 'User')} #${id}`, first_name: '', last_name: '' };
        return {
          user_id: id,
          username: user.username,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
        };
      });
    } catch {
      return uniqueIds.map((id) => ({ user_id: id, username: `${t('tasks_user_fallback', 'User')} #${id}`, first_name: '', last_name: '' }));
    }
  };

  const buildClockStatusFallback = async (task, date) => {
    const taskDate = normalizeDateOnly(date || task?.task_date || today);
    let members = [];

    if (task?.team_id) {
      try {
        const response = await teamsAPI.getMembers(task.team_id);
        members = (response.data || []).map((m) => ({
          user_id: Number(m.user_id),
          username: m.username,
          first_name: m.first_name || '',
          last_name: m.last_name || '',
        }));
      } catch {
        members = [];
      }
    }

    if (members.length === 0) {
      const ids = parseMemberIds(task);
      members = await resolveMembersByIds(ids);
    }

    if (members.length === 0) {
      return {
        taskDate,
        plannedAvailableMembers: [],
        clockedInMembers: [],
        notClockedInMembers: [],
      };
    }

    const statuses = await Promise.all(members.map(async (member) => {
      try {
        const response = await availabilityAPI.getPlanned(member.user_id);
        const day = (response.data || []).find((row) => normalizeDateOnly(row.availability_date) === taskDate);
        const isPlannedAvailable = !!day && Number(day.available) === 1;
        const isClockedIn = isPlannedAvailable && !!day.login_time && !day.logout_time;
        return {
          ...member,
          isPlannedAvailable,
          isClockedIn,
          clock_in_time: isClockedIn ? day.login_time : null,
        };
      } catch {
        return { ...member, isPlannedAvailable: false, isClockedIn: false, clock_in_time: null };
      }
    }));

    return {
      taskDate,
      plannedAvailableMembers: statuses.filter((m) => m.isPlannedAvailable),
      clockedInMembers: statuses.filter((m) => m.isClockedIn),
      notClockedInMembers: statuses.filter((m) => !m.isClockedIn),
    };
  };

  const resolveTaskMemberIds = async (task) => {
    if (!task) return [];

    let memberIds = [];
    if (task.team_id && task.status === 'pending') {
      try {
        const response = await teamsAPI.getMembers(task.team_id);
        memberIds = (response.data || [])
          .map((member) => Number(member.user_id))
          .filter((id) => Number.isFinite(id) && id > 0);
      } catch {
        memberIds = [];
      }
    }

    if (memberIds.length === 0) {
      memberIds = parseMemberIds(task);
    }

    if (memberIds.length === 0 && task.team_id) {
      try {
        const response = await teamsAPI.getMembers(task.team_id);
        memberIds = (response.data || [])
          .map((member) => Number(member.user_id))
          .filter((id) => Number.isFinite(id) && id > 0);
      } catch {
        memberIds = [];
      }
    }

    return toUniqueIds(memberIds);
  };

  const buildStatusUpdatePayload = async (task, targetStatus, memberIdsOverride = null) => {
    const payload = { status: targetStatus };
    const requiresClockCheck = targetStatus === 'in_progress'
      || (targetStatus === 'completed' && task?.status === 'pending');

    if (!requiresClockCheck) {
      return payload;
    }

    payload.taskDate = normalizeDateOnly(task?.task_date);
    const memberIds = memberIdsOverride === null
      ? await resolveTaskMemberIds(task)
      : toUniqueIds(memberIdsOverride);

    if (memberIds.length > 0) {
      payload.memberIds = memberIds;
    }

    return payload;
  };

  const buildEditTaskPayload = (memberIdsOverride = null) => ({
    taskDate: editTask.taskDate,
    title: editTask.title,
    description: editTask.description,
    ...(canViewFinancials ? { revenueAmount: Number.isFinite(Number(editTask.revenueAmount)) ? Number(editTask.revenueAmount) : 0 } : {}),
    jobTypeId: editTask.jobTypeId ? Number(editTask.jobTypeId) : null,
    teamId: editTask.teamId ? Number(editTask.teamId) : null,
    status: editTask.status,
    priority: editTask.priority,
    memberIds: toUniqueIds(memberIdsOverride === null ? editTaskMemberIds : memberIdsOverride),
    resources: normalizeTaskResourceItems(editTaskResources),
  });

  const buildRevenueOnlyEditPayload = () => ({
    ...(canViewFinancials ? { revenueAmount: Number.isFinite(Number(editTask.revenueAmount)) ? Number(editTask.revenueAmount) : 0 } : {}),
  });

  const buildCompletedTaskEditPayload = () => ({
    ...(canViewFinancials ? { revenueAmount: Number.isFinite(Number(editTask.revenueAmount)) ? Number(editTask.revenueAmount) : 0 } : {}),
    status: editTask.status,
    startTime: editTask.startTime || null,
    endTime: editTask.endTime || null,
  });

  const closeClockInPopup = () => {
    setClockInPopup(createEmptyClockInPopup());
  };

  const showClockInPopupFromError = async (data, context = {}) => {
    const hasClockData = Boolean(
      data?.plannedAvailableMembers
      || data?.notClockedInMembers
      || data?.clockedInMembers
      || data?.plannedAvailable
      || data?.notClockedIn
      || data?.clockedIn
    );
    if (!hasClockData && !context.task) return false;

    let notClockedInMembers = Array.isArray(data?.notClockedInMembers) ? data.notClockedInMembers : [];
    let plannedAvailableMembers = Array.isArray(data?.plannedAvailableMembers) ? data.plannedAvailableMembers : [];
    let clockedInMembers = Array.isArray(data?.clockedInMembers) ? data.clockedInMembers : [];

    if (notClockedInMembers.length === 0 && Array.isArray(data?.notClockedIn) && data.notClockedIn.length > 0) {
      notClockedInMembers = await resolveMembersByIds(data.notClockedIn);
    }
    if (plannedAvailableMembers.length === 0 && Array.isArray(data?.plannedAvailable) && data.plannedAvailable.length > 0) {
      plannedAvailableMembers = await resolveMembersByIds(data.plannedAvailable);
    }
    if (clockedInMembers.length === 0 && Array.isArray(data?.clockedIn) && data.clockedIn.length > 0) {
      clockedInMembers = await resolveMembersByIds(data.clockedIn);
    }

    if (!hasClockData && context.task) {
      const fallback = await buildClockStatusFallback(context.task, context.taskDate);
      plannedAvailableMembers = fallback.plannedAvailableMembers;
      clockedInMembers = fallback.clockedInMembers;
      notClockedInMembers = fallback.notClockedInMembers;
      context.taskDate = fallback.taskDate;
    }

    setClockInPopup({
      open: true,
      sourceMode: context.sourceMode || 'drag',
      targetStatus: context.targetStatus || 'in_progress',
      taskId: context.taskId || null,
      taskTitle: context.taskTitle || t('tasks_task', 'Task'),
      taskDate: data?.taskDate || context.taskDate || '',
      plannedAvailableMembers,
      clockedInMembers,
      notClockedInMembers,
      pendingPayload: context.pendingPayload || null,
      submitting: false,
    });
    return true;
  };

  const handleProceedAnyway = async () => {
    if (!clockInPopup.taskId || !clockInPopup.pendingPayload || clockInPopup.submitting) return;

    const removedIds = new Set(clockInPopup.notClockedInMembers.map((member) => Number(member.user_id)));
    const nextMemberIds = toUniqueIds(
      (clockInPopup.pendingPayload.memberIds || []).filter((id) => !removedIds.has(Number(id)))
    );

    try {
      setClockInPopup((prev) => ({ ...prev, submitting: true }));
      await tasksAPI.update(clockInPopup.taskId, {
        ...clockInPopup.pendingPayload,
        memberIds: nextMemberIds,
      });
      if (clockInPopup.sourceMode === 'edit') {
        setShowEditModal(false);
        setTeamMembers([]);
        setEditTaskMemberIds(nextMemberIds);
      }
      closeClockInPopup();
      fetchTasks();
    } catch (error) {
      alert(error.response?.data?.message || t('tasks_update_failed', 'Failed to update task'));
      setClockInPopup((prev) => ({ ...prev, submitting: false }));
    }
  };

  const columns = [
    { id: 'pending', title: t('tasks_status_pending', 'Pending') },
    { id: 'in_progress', title: t('tasks_status_in_progress', 'In Progress') },
    { id: 'completed', title: t('tasks_status_completed', 'Completed') },
    { id: 'cancelled', title: t('tasks_status_cancelled', 'Cancelled') },
  ];
  const canViewCancelledFilter = user?.role !== 'user';
  const useStackedHeaderLayout = false;

  const fetchTasks = useCallback(async (forcedParams = null) => {
    const requestId = ++fetchTasksRequestIdRef.current;
    try {
      setLoading(true);
      setErrorMessage('');
      let params = {};
      if (forcedParams && typeof forcedParams === 'object') {
        params = forcedParams;
      } else {
        const hasRangeFilter = Boolean(startDate || endDate);
        if (hasRangeFilter) {
          if (startDate) params.startDate = startDate;
          if (endDate) params.endDate = endDate;
        } else {
          params.date = today;
        }
      }
      const response = await tasksAPI.getAll(Object.keys(params).length > 0 ? params : undefined);
      setTasks(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setTasks([]);
        setErrorMessage(t('tasks_permission_view_denied', 'You no longer have permission to view tasks.'));
        refreshPermissions();
        return;
      }
      console.error('Failed to fetch tasks:', error);
    } finally {
      if (requestId === fetchTasksRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, today, refreshPermissions, t]);

  const fetchJobTypes = useCallback(async () => {
    try {
      const response = await jobTypesAPI.getAll();
      setJobTypes(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setJobTypes([]);
        return;
      }
      console.error('Failed to fetch job types:', error);
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setTeams([]);
        return;
      }
      console.error('Failed to fetch teams:', error);
    }
  }, []);

  const fetchResourcesCatalog = useCallback(async () => {
    try {
      const response = await resourcesAPI.getAll();
      const resources = (response.data || []).filter((resource) => resource.status === 'active');
      setResourcesCatalog(resources);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      setResourcesCatalog([]);
    }
  }, []);

  useEffect(() => {
    if (!canViewTasks) {
      setLoading(false);
      setErrorMessage(t('tasks_permission_view_denied', 'You no longer have permission to view tasks.'));
      return;
    }

    fetchTasks();
    if (canManageTasks) {
      fetchJobTypes();
      fetchTeams();
      fetchResourcesCatalog();
    }
  }, [canViewTasks, canManageTasks, fetchTasks, t, fetchJobTypes, fetchTeams, fetchResourcesCatalog]);

  const handleAddTaskResource = (mode) => {
    if (mode === 'create') {
      const resourceId = Number(newTaskResourceInput.resourceId);
      const quantity = Number(newTaskResourceInput.quantity);
      if (!resourceId || quantity <= 0) return;
      setNewTaskResources((prev) => normalizeTaskResourceItems([...prev, { resourceId, quantity }]));
      setNewTaskResourceInput({ resourceId: '', quantity: 1 });
      return;
    }

    const resourceId = Number(editTaskResourceInput.resourceId);
    const quantity = Number(editTaskResourceInput.quantity);
    if (!resourceId || quantity <= 0) return;
    setEditTaskResources((prev) => normalizeTaskResourceItems([...prev, { resourceId, quantity }]));
    setEditTaskResourceInput({ resourceId: '', quantity: 1 });
  };

  const handleRemoveTaskResource = (mode, resourceId) => {
    if (mode === 'create') {
      setNewTaskResources((prev) => prev.filter((item) => Number(item.resourceId) !== Number(resourceId)));
      return;
    }
    setEditTaskResources((prev) => prev.filter((item) => Number(item.resourceId) !== Number(resourceId)));
  };

  const getResourceLabel = (resourceId) => {
    const resource = resourcesCatalog.find((item) => Number(item.id) === Number(resourceId));
    if (!resource) return `${t('tasks_resource_fallback', 'Resource')} #${resourceId}`;
    return `${resource.name} (${resource.available} ${t('tasks_available', 'available')})`;
  };

  const createResourceOptions = resourcesCatalog.filter((resource) => Number(resource.available) > 0);
  const editSelectedResourceIds = new Set(editTaskResources.map((item) => Number(item.resourceId)));
  const editResourceOptions = resourcesCatalog.filter(
    (resource) => Number(resource.available) > 0 || editSelectedResourceIds.has(Number(resource.id))
  );

  const fetchTeamMembers = async (teamId) => {
    if (!teamId) { setTeamMembers([]); return; }
    try {
      const response = await teamsAPI.getMembers(teamId);
      setTeamMembers(response.data);
    } catch {
      setTeamMembers([]);
    }
  };

  const handleTeamChangeEdit = async (teamIdStr) => {
    const teamId = teamIdStr ? Number(teamIdStr) : '';
    setEditTask({ ...editTask, teamId });
    if (!teamId) {
      setTeamMembers([]);
      setEditTaskMemberIds([]);
      return;
    }
    try {
      const response = await teamsAPI.getMembers(teamId);
      setTeamMembers(response.data);
      setEditTaskMemberIds(response.data.map(m => Number(m.user_id)));
    } catch {
      setTeamMembers([]);
      setEditTaskMemberIds([]);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksAPI.create({
        ...newTask,
        ...(canViewFinancials ? { revenueAmount: Number.isFinite(Number(newTask.revenueAmount)) ? Number(newTask.revenueAmount) : 0 } : {}),
        jobTypeId: Number(newTask.jobTypeId),
        teamId: newTask.teamId ? Number(newTask.teamId) : null,
        startTime: newTask.startTime || null,
        endTime: newTask.endTime || null,
        memberIds: teamMembers.map(m => m.user_id),
        resources: normalizeTaskResourceItems(newTaskResources),
      });
      setNewTask({
        taskDate: getTodayKualaLumpurISODate(),
        title: '',
        description: '',
        revenueAmount: '',
        jobTypeId: '',
        teamId: '',
        priority: 'medium',
        startTime: '',
        endTime: ''
      });
      setTeamMembers([]);
      setNewTaskResources([]);
      setNewTaskResourceInput({ resourceId: '', quantity: 1 });
      setShowCreateModal(false);
      fetchTasks();
      fetchResourcesCatalog();
    } catch (error) {
      console.error('Failed to create task:', error);
      alert(`${t('tasks_create_failed_prefix', 'Error creating task')}: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleQuickStatusChange = async (task, newStatus) => {
    if (!can('tasks', 'edit')) return;

    try {
      const payload = await buildStatusUpdatePayload(task, newStatus);
      
      await tasksAPI.update(task.id, payload);
      fetchTasks(); // Refresh tasks
    } catch (error) {
      const data = error?.response?.data;
      const shouldCheck = newStatus === 'in_progress' || (newStatus === 'completed' && task?.status === 'pending');
      const shown = shouldCheck
        ? await showClockInPopupFromError(data, {
          sourceMode: 'button',
          taskId: task.id,
          taskTitle: task?.title,
          taskDate: normalizeDateOnly(task?.task_date),
          targetStatus: newStatus,
          pendingPayload: await buildStatusUpdatePayload(task, newStatus),
          task,
        })
        : false;
      if (!shown) {
        alert(data?.message || t('tasks_update_failed', 'Failed to update task'));
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!can('tasks', 'edit')) return;

    const { destination, draggableId } = result;

    if (!destination) return;
    if (result.source.droppableId === destination.droppableId) return;

    const taskId = draggableId;
    const newStatus = destination.droppableId;
    const task = tasks.find((t) => String(t.id) === String(taskId));
    let payload = null;

    try {
      payload = await buildStatusUpdatePayload(task, newStatus);

      await tasksAPI.update(taskId, payload);
      fetchTasks(); // Refresh tasks
    } catch (error) {
      const data = error?.response?.data;
      const shouldCheck = newStatus === 'in_progress' || (newStatus === 'completed' && task?.status === 'pending');
      const shown = shouldCheck
        ? await showClockInPopupFromError(data, {
          sourceMode: 'drag',
          taskId,
          taskTitle: task?.title,
          taskDate: normalizeDateOnly(task?.task_date),
          targetStatus: newStatus,
          pendingPayload: payload,
          task,
        })
        : false;
      if (!shown) {
        alert(data?.message || t('tasks_update_failed', 'Failed to update task'));
      }
    }
  };

  const handleTaskClick = async (task) => {
    if (!can('tasks', 'edit')) return;
    if (task.status === 'in_progress') {
      alert(t('tasks_in_progress_status_move_only', 'In Progress task cannot be edited. Move it using Kanban status only.'));
      return;
    }
    if (task.status !== 'pending' && task.status !== 'completed') {
      alert(t('tasks_only_pending_or_completed_editable', 'Only pending or completed tasks can be edited.'));
      return;
    }
    
    let memberIdsToSet = [];
    if (task.team_id) {
      try {
        const response = await teamsAPI.getMembers(task.team_id);
        setTeamMembers(response.data);
        memberIdsToSet = response.data.map(m => Number(m.user_id));
      } catch {
        setTeamMembers([]);
        memberIdsToSet = [];
      }
    } else {
      setTeamMembers([]);
    }
    
    setEditTaskMemberIds(memberIdsToSet);

    let taskResources = [];
    try {
      const detail = await tasksAPI.getById(task.id);
      taskResources = (detail.data?.resources || []).map((item) => ({
        resourceId: Number(item.resource_id),
        quantity: Number(item.quantity_required || item.quantity_allocated || 0),
      })).filter((item) => item.resourceId > 0 && item.quantity > 0);
    } catch {
      taskResources = [];
    }

    setEditTaskResources(normalizeTaskResourceItems(taskResources));
    setEditTaskResourceInput({ resourceId: '', quantity: 1 });
    // Ensure taskDate is just the date part (YYYY-MM-DD), not a full ISO timestamp
    const taskDate = task.task_date ? task.task_date.split('T')[0] : '';
    setEditTask({
      id: task.id,
      originalStatus: task.status,
      taskDate,
      title: task.title,
      description: task.description || '',
      revenueAmount: task.revenue_amount !== undefined && task.revenue_amount !== null ? String(task.revenue_amount) : '',
      jobTypeId: task.job_type_id || '',
      teamId: task.team_id ? Number(task.team_id) : '',
      status: task.status,
      priority: task.priority || 'medium',
      startTime: task.start_time || '',
      endTime: task.end_time || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const isCompletedTaskEdit = editTask.originalStatus === 'completed';
      const payload = isCompletedTaskEdit ? buildCompletedTaskEditPayload() : buildEditTaskPayload();
      await tasksAPI.update(editTask.id, payload);
      setShowEditModal(false);
      setTeamMembers([]);
      setEditTaskResources([]);
      setEditTaskResourceInput({ resourceId: '', quantity: 1 });
      fetchTasks();
      fetchResourcesCatalog();
    } catch (error) {
      const data = error?.response?.data;
      const shouldCheck = editTask.status === 'in_progress' || editTask.status === 'completed';
      const shown = shouldCheck
        ? await showClockInPopupFromError(data, {
          sourceMode: 'edit',
          taskId: editTask.id,
          taskTitle: editTask.title,
          taskDate: editTask.taskDate,
          targetStatus: editTask.status,
          pendingPayload: editTask.originalStatus === 'completed' ? buildCompletedTaskEditPayload() : buildEditTaskPayload(),
        })
        : false;
      if (shown) {
        return;
      }
      alert(data?.message || t('tasks_update_failed', 'Failed to update task'));
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm(`${t('tasks_delete_confirm_prefix', 'Delete task')} ${editTask.title}?`)) return;
    try {
      await tasksAPI.delete(editTask.id);
      setShowEditModal(false);
      fetchTasks();
    } catch (error) {
      alert(error.response?.data?.message || t('tasks_delete_failed', 'Failed to delete task'));
    }
  };

  const getTasksByStatus = (status) => (
    tasks
      .filter((task) => task.status === status)
      .sort((a, b) => {
        const dateA = normalizeDateOnly(a.task_date);
        const dateB = normalizeDateOnly(b.task_date);
        if (dateA !== dateB) return dateB.localeCompare(dateA);
        return Number(b.id) - Number(a.id);
      })
  );

  const handleResetToToday = () => {
    setStartDate(today);
    setEndDate(today);
    fetchTasks({ date: today });
  };

  const handleStatusToggle = (statusId) => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [statusId]: !prev[statusId],
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('tasks_page_title', 'Daily Tasks')}</h1>
        <div className={`${styles.headerControls} ${useStackedHeaderLayout ? styles.userHeaderControls : ''}`}>
          <div className={`${styles.statusVisibilityControls} ${useStackedHeaderLayout ? styles.userStatusVisibilityControls : ''}`}>
            {columns.filter((column) => canViewCancelledFilter || column.id !== 'cancelled').map((column) => (
              <label key={column.id} className={styles.statusCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(visibleStatuses[column.id])}
                  onChange={() => handleStatusToggle(column.id)}
                />
                <span>{column.title}</span>
              </label>
            ))}
          </div>
          <div className={`${styles.dateFilter} ${useStackedHeaderLayout ? styles.userDateFilter : ''}`}>
            <div className={styles.dateField}>
              <span className={styles.dateLabel}>{t('tasks_filter_from', 'From')}</span>
              <DatePicker
                value={startDate}
                onChange={(val) => setStartDate(val)}
                placeholder={t('tasks_start_date', 'Start date')}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.dateField}>
              <span className={styles.dateLabel}>{t('tasks_filter_to', 'To')}</span>
              <DatePicker
                value={endDate}
                onChange={(val) => setEndDate(val)}
                placeholder={t('tasks_end_date', 'End date')}
                className={styles.dateInput}
              />
            </div>
            <button
              onClick={handleResetToToday}
              className={styles.resetBtn}
              aria-label={t('tasks_reset_to_today', 'Reset to today')}
              title={t('tasks_reset_to_today', 'Reset to today')}
            >
              <i className="bi bi-calendar-day" aria-hidden="true"></i>
            </button>
            <button
              onClick={fetchTasks}
              className={styles.refreshBtn}
              aria-label={t('tasks_refresh_tasks', 'Refresh tasks')}
              title={t('tasks_refresh', 'Refresh')}
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
            </button>
            <button
              onClick={() => setCollapsedView(!collapsedView)}
              className={styles.collapseBtn}
              aria-label={collapsedView ? t('tasks_expand_all', 'Expand all') : t('tasks_collapse_all', 'Collapse all')}
              title={collapsedView ? t('tasks_expand_all', 'Expand all') : t('tasks_collapse_all', 'Collapse all')}
            >
              <i className={`bi bi-${collapsedView ? 'arrows-expand' : 'arrows-collapse'}`} aria-hidden="true"></i>
            </button>
          </div>
          {can('tasks', 'create') && (
            <button className={styles.createBtn} onClick={() => {
              setTeamMembers([]);
              setNewTaskResources([]);
              setNewTaskResourceInput({ resourceId: '', quantity: 1 });
              setShowCreateModal(true);
            }}>+ {t('tasks_create_task', 'Create Task')}</button>
          )}
        </div>
      </div>

      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

      {loading ? (
        <div className={styles.loading}>{t('tasks_loading', 'Loading tasks...')}</div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className={styles.kanbanBoard}>
            {columns
              .filter((column) => visibleStatuses[column.id])
              .map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                tasks={getTasksByStatus(column.id)}
                onTaskClick={handleTaskClick}
                onQuickStatusChange={handleQuickStatusChange}
                canViewFinancials={canViewFinancials}
                canEditTasks={can('tasks', 'edit')}
                collapsedView={collapsedView}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {showCreateModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('tasks_modal_create_title', 'Create New Task')}</h2>
            <form onSubmit={handleCreateTask}>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_title', 'Title')} *</label>
                <input
                  type="text"
                  required
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder={t('tasks_placeholder_title', 'Enter task title')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_date', 'Date')} *</label>
                <DatePicker
                  value={newTask.taskDate}
                  onChange={(val) => setNewTask({ ...newTask, taskDate: val })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_job_type', 'Job Type')} *</label>
                <select
                  required
                  value={newTask.jobTypeId}
                  onChange={(e) => setNewTask({ ...newTask, jobTypeId: e.target.value })}
                >
                  <option value="">{t('tasks_select_job_type', 'Select a job type')}</option>
                  {jobTypes.map(jobType => (
                    <option key={jobType.id} value={jobType.id}>
                      {jobType.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_team', 'Team')}</label>
                <select
                  value={newTask.teamId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewTask({ ...newTask, teamId: val });
                    fetchTeamMembers(val);
                  }}
                >
                  <option value="">{t('tasks_select_team_optional', 'Select a team (optional)')}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_task_resources', 'Task Resources')}</label>
                <div className={styles.resourceEditorRow}>
                  <select
                    value={newTaskResourceInput.resourceId}
                    onChange={(e) => setNewTaskResourceInput({ ...newTaskResourceInput, resourceId: e.target.value })}
                  >
                    <option value="">{t('tasks_select_resource', 'Select resource')}</option>
                    {createResourceOptions.map((resource) => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name} ({resource.available} available)
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={newTaskResourceInput.quantity}
                    onChange={(e) => setNewTaskResourceInput({ ...newTaskResourceInput, quantity: Number(e.target.value) })}
                  />
                  <button type="button" className={styles.smallActionBtn} onClick={() => handleAddTaskResource('create')}>
                    {t('common_add', 'Add')}
                  </button>
                </div>
                <div className={styles.resourceTagList}>
                  {newTaskResources.map((item) => (
                    <span key={`new-res-${item.resourceId}`} className={styles.resourceTag}>
                      {getResourceLabel(item.resourceId)} x{item.quantity}
                      <button type="button" onClick={() => handleRemoveTaskResource('create', item.resourceId)}>x</button>
                    </span>
                  ))}
                  {newTaskResources.length === 0 && (
                    <p className={styles.resourceHint}>{t('tasks_no_resources_assigned', 'No resources assigned to this task.')}</p>
                  )}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')} *</label>
                <textarea
                  required
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder={t('tasks_placeholder_description', 'Enter task description')}
                />
              </div>
              {canViewFinancials && (
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_revenue_amount_rm', 'Revenue Amount (RM)')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newTask.revenueAmount}
                    onChange={(e) => setNewTask({ ...newTask, revenueAmount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              )}
              <div className={styles.formGroup}>
                <label>{t('tasks_label_priority', 'Priority')}</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                >
                  <option value="low">{t('tasks_priority_low', 'Low')}</option>
                  <option value="medium">{t('tasks_priority_medium', 'Medium')}</option>
                  <option value="high">{t('tasks_priority_high', 'High')}</option>
                </select>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_start_time', 'Start Time')}</label>
                  <input
                    type="time"
                    value={newTask.startTime}
                    onChange={(e) => setNewTask({ ...newTask, startTime: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_end_time', 'End Time')}</label>
                  <input
                    type="time"
                    value={newTask.endTime}
                    onChange={(e) => setNewTask({ ...newTask, endTime: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>{t('tasks_create_task', 'Create Task')}</button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTaskResources([]);
                    setNewTaskResourceInput({ resourceId: '', quantity: 1 });
                  }}
                >
                  {t('common_cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('tasks_modal_edit_title', 'Edit Task')}</h2>
            <form onSubmit={handleUpdateTask}>
              {editTask.originalStatus === 'completed' && (
                <p className={styles.resourceHint}>
                  {t('tasks_completed_status_revenue_time_only_hint', 'Completed task can update status, revenue amount, start time, and end time only.')}
                </p>
              )}
              <div className={styles.formGroup}>
                <label>{t('tasks_label_title', 'Title')} *</label>
                <input
                  type="text"
                  required
                  value={editTask.title}
                  onChange={(e) => setEditTask({ ...editTask, title: e.target.value })}
                  disabled={editTask.originalStatus === 'completed'}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_date', 'Date')} *</label>
                <DatePicker
                  value={editTask.taskDate}
                  onChange={(val) => setEditTask({ ...editTask, taskDate: val })}
                  required
                  disabled={editTask.originalStatus === 'completed'}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_job_type', 'Job Type')} *</label>
                <select
                  required
                  value={editTask.jobTypeId}
                  onChange={(e) => setEditTask({ ...editTask, jobTypeId: e.target.value })}
                  disabled={editTask.originalStatus === 'completed'}
                >
                  <option value="">{t('tasks_select_job_type', 'Select a job type')}</option>
                  {jobTypes.map((jobType) => (
                    <option key={jobType.id} value={jobType.id}>{jobType.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_team', 'Team')}</label>
                <select
                  value={editTask.teamId}
                  onChange={(e) => handleTeamChangeEdit(e.target.value)}
                  disabled={editTask.originalStatus === 'completed'}
                >
                  <option value="">{t('tasks_unassigned', 'Unassigned')}</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_task_resources', 'Task Resources')}</label>
                <div className={styles.resourceEditorRow}>
                  <select
                    value={editTaskResourceInput.resourceId}
                    onChange={(e) => setEditTaskResourceInput({ ...editTaskResourceInput, resourceId: e.target.value })}
                    disabled={editTask.originalStatus === 'completed'}
                  >
                    <option value="">{t('tasks_select_resource', 'Select resource')}</option>
                    {editResourceOptions.map((resource) => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name} ({resource.available} available)
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={editTaskResourceInput.quantity}
                    onChange={(e) => setEditTaskResourceInput({ ...editTaskResourceInput, quantity: Number(e.target.value) })}
                    disabled={editTask.originalStatus === 'completed'}
                  />
                  <button type="button" className={styles.smallActionBtn} onClick={() => handleAddTaskResource('edit')} disabled={editTask.originalStatus === 'completed'}>
                    {t('common_add', 'Add')}
                  </button>
                </div>
                <div className={styles.resourceTagList}>
                  {editTaskResources.map((item) => (
                    <span key={`edit-res-${item.resourceId}`} className={styles.resourceTag}>
                      {getResourceLabel(item.resourceId)} x{item.quantity}
                      <button type="button" onClick={() => handleRemoveTaskResource('edit', item.resourceId)} disabled={editTask.originalStatus === 'completed'}>x</button>
                    </span>
                  ))}
                  {editTaskResources.length === 0 && (
                    <p className={styles.resourceHint}>{t('tasks_no_resources_assigned', 'No resources assigned to this task.')}</p>
                  )}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_status', 'Status')}</label>
                <select
                  value={editTask.status}
                  onChange={(e) => setEditTask({ ...editTask, status: e.target.value })}
                >
                  <option value="pending">{t('tasks_status_pending', 'Pending')}</option>
                  <option value="in_progress">{t('tasks_status_in_progress', 'In Progress')}</option>
                  <option value="completed">{t('tasks_status_completed', 'Completed')}</option>
                  <option value="cancelled">{t('tasks_status_cancelled', 'Cancelled')}</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_priority', 'Priority')}</label>
                <select
                  value={editTask.priority}
                  onChange={(e) => setEditTask({ ...editTask, priority: e.target.value })}
                  disabled={editTask.originalStatus === 'completed'}
                >
                  <option value="low">{t('tasks_priority_low', 'Low')}</option>
                  <option value="medium">{t('tasks_priority_medium', 'Medium')}</option>
                  <option value="high">{t('tasks_priority_high', 'High')}</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')} *</label>
                <textarea
                  required
                  value={editTask.description}
                  onChange={(e) => setEditTask({ ...editTask, description: e.target.value })}
                  disabled={editTask.originalStatus === 'completed'}
                />
              </div>
              {canViewFinancials && (
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_revenue_amount_rm', 'Revenue Amount (RM)')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editTask.revenueAmount}
                    onChange={(e) => setEditTask({ ...editTask, revenueAmount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              )}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_start_time', 'Start Time')}</label>
                  <input
                    type="time"
                    value={editTask.startTime}
                    onChange={(e) => setEditTask({ ...editTask, startTime: e.target.value })}
                    disabled={editTask.originalStatus !== 'completed'}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('tasks_label_end_time', 'End Time')}</label>
                  <input
                    type="time"
                    value={editTask.endTime}
                    onChange={(e) => setEditTask({ ...editTask, endTime: e.target.value })}
                    disabled={editTask.originalStatus !== 'completed'}
                  />
                </div>
              </div>
              <div className={styles.modalButtons}>
                {can('tasks', 'delete') && (
                  <button type="button" className={styles.deleteBtn} onClick={handleDeleteTask}>{t('tasks_delete_button', 'Delete')}</button>
                )}
                <button type="submit" className={styles.submitBtn}>{t('common_save', 'Save')}</button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => {
                    setShowEditModal(false);
                    setEditTaskResources([]);
                    setEditTaskResourceInput({ resourceId: '', quantity: 1 });
                  }}
                >
                  {t('common_cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {clockInPopup.open && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>
              {clockInPopup.targetStatus === 'completed'
                ? t('tasks_popup_title_completing', 'Members Need Attention Before Completing')
                : t('tasks_popup_title_starting', 'Members Need Attention Before Starting')}
            </h2>
            <p className={styles.clockInPopupMeta}>
              <strong>{clockInPopup.taskTitle}</strong>
              {clockInPopup.taskDate ? ` (${clockInPopup.taskDate})` : ''}
            </p>

            <p className={styles.clockInPopupHelp}>
              {t('tasks_popup_help', 'Proceeding will remove the members below who are not clocked in from this task only. Their team membership stays unchanged.')}
            </p>

            <div className={styles.clockInPopupSection}>
              <h3>{t('tasks_popup_not_clocked_in', 'Not Clocked In')}</h3>
              {clockInPopup.notClockedInMembers.length === 0 ? (
                <p className={styles.clockInPopupEmpty}>{t('tasks_popup_none', 'None')}</p>
              ) : (
                <ul className={styles.clockInPopupList}>
                  {clockInPopup.notClockedInMembers.map((member) => (
                    <li key={member.user_id} className={styles.clockInPopupItemNotIn}>
                      {formatMemberLabel(member)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.clockInPopupSection}>
              <h3>{t('tasks_popup_planned_available', 'Planned Available On Task Day')}</h3>
              {clockInPopup.plannedAvailableMembers.length === 0 ? (
                <p className={styles.clockInPopupEmpty}>{t('tasks_popup_none', 'None')}</p>
              ) : (
                <ul className={styles.clockInPopupList}>
                  {clockInPopup.plannedAvailableMembers.map((member) => (
                    <li key={member.user_id} className={styles.clockInPopupItemPlanned}>
                      {formatMemberLabel(member)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.clockInPopupSection}>
              <h3>{t('tasks_popup_clocked_in', 'Clocked In')}</h3>
              {clockInPopup.clockedInMembers.length === 0 ? (
                <p className={styles.clockInPopupEmpty}>{t('tasks_popup_none', 'None')}</p>
              ) : (
                <ul className={styles.clockInPopupList}>
                  {clockInPopup.clockedInMembers.map((member) => (
                    <li key={member.user_id} className={styles.clockInPopupItemIn}>
                      <div>{formatMemberLabel(member)}</div>
                      {formatClockInTime(member.clock_in_time) ? (
                        <div className={styles.clockInPopupTime}>{t('tasks_popup_clocked_in_at', 'Clocked in at')} {formatClockInTime(member.clock_in_time)}</div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.modalButtons}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={closeClockInPopup}
              >
                {t('common_cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className={styles.proceedBtn}
                onClick={handleProceedAnyway}
                disabled={clockInPopup.submitting}
              >
                {clockInPopup.submitting ? t('tasks_popup_proceeding', 'Proceeding...') : t('tasks_popup_proceed_anyway', 'Proceed Anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
